import Box from '@mui/material/Box';
import { mdiLightbulbOutline } from '@mdi/js';
import { motion } from 'framer-motion';

import {
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    CardMediaIcon,
    CardSubtitle,
    CardTitle,
} from 'common/components/Card';
import { Icon } from 'common/components/Icon';
import {
    IPortfolioItemListItem,
    IBasicFileFragment,
    Maybe,
} from 'graphql/types';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { useSkillStatusDescription } from 'skill/hooks/useSkillStatusDescription';

interface ISkill {
    id: string;
    name: string;
    thumbnail?: Maybe<IBasicFileFragment>;
}

interface IProps {
    skill: ISkill;
    portfolioItem?: Maybe<
        Pick<
            IPortfolioItemListItem,
            | 'relatedType'
            | 'score'
            | 'scoreType'
            | 'scoreThreshold'
            | 'scoreMax'
            | 'progress'
            | 'startDate'
            | 'endDate'
            | 'completionDate'
            | 'relatedObjectHasComments'
            | 'relatedObjectHasFiles'
            | 'isCompliant'
        >
    >;
    onClick?(skill: ISkill): void;
}

export const SkillCard = ({ skill, portfolioItem, onClick }: IProps) => {
    const { name, thumbnail } = skill;

    const hasImage = !!thumbnail?.url;

    const statusDescription = useSkillStatusDescription(portfolioItem);

    return (
        <Card>
            <motion.div
                initial="initCardButton"
                style={{ height: '100%' }}
                whileHover="hoverCardButton"
            >
                <CardActionArea onClick={() => onClick?.(skill)}>
                    <CardMedia
                        image={thumbnail?.url || ''}
                        sx={{
                            height: '160px',
                            color: 'common.white',
                            bgcolor: 'primary.main',
                        }}
                    >
                        <motion.div
                            variants={{
                                initCardButton: {
                                    opacity: hasImage ? 0 : 1,
                                },
                                hoverCardButton: {
                                    opacity: 1,
                                    transition: { duration: 0.2 },
                                },
                            }}
                        >
                            <CardMediaIcon>
                                <Icon path={mdiLightbulbOutline} size="3rem" />
                            </CardMediaIcon>
                        </motion.div>
                    </CardMedia>

                    <Box
                        sx={{
                            position: 'relative',
                            pt: portfolioItem !== undefined ? 1 : 0,
                        }}
                    >
                        {portfolioItem !== undefined && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: (theme) => theme.spacing(3),
                                    transform: 'translateY(-50%)',
                                }}
                            >
                                <PortfolioItemProgressScoreChip
                                    withIcon
                                    portfolioItem={portfolioItem}
                                    size="large"
                                    sx={{
                                        border: '5px solid',
                                        borderColor: 'common.white',
                                        boxSizing: 'content-box',
                                    }}
                                />
                            </Box>
                        )}

                        <CardContent>
                            <CardTitle>{name}</CardTitle>

                            {statusDescription && (
                                <CardSubtitle>{statusDescription}</CardSubtitle>
                            )}
                        </CardContent>
                    </Box>
                </CardActionArea>
            </motion.div>
        </Card>
    );
};
